// Week math for the roster grid. Weeks run Friday → Thursday, matching the
// kennel's rota sheet. All dates are ISO (yyyy-mm-dd) and parsed at local
// midnight so no timezone drift creeps in.

import { DOW_ABBR, MONTHS_ABBR, daysBetween } from '../../lib/dates';

function parse(iso: string): Date {
  return new Date(iso + 'T00:00:00');
}

// Short weekday name for an ISO date, e.g. "Fri". The single home for day-name
// formatting (the staff self-view reads it) so no caller redeclares the array.
export function dayName(dateIso: string): string {
  return DOW_ABBR[parse(dateIso).getDay()]!;
}

// "19 June" — day-of-month + full month, the per-day label idiom (staff roster).
export function dayAndMonth(dateIso: string): string {
  const d = parse(dateIso);
  return `${d.getDate()} ${d.toLocaleDateString('en-IE', { month: 'long' })}`;
}
function iso(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// The Friday that starts the week containing `dateIso`.
export function startOfWeek(dateIso: string): string {
  const d = parse(dateIso);
  const dow = d.getDay(); // 0=Sun..6=Sat
  const back = (dow + 2) % 7; // days since the week's Friday (Fri=0 … Thu=6)
  d.setDate(d.getDate() - back);
  return iso(d);
}

// The 7 ISO dates of a week, given its starting Friday.
export function weekDates(fridayIso: string): string[] {
  const start = parse(fridayIso);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return iso(d);
  });
}

// Shift a week-start Friday by N weeks.
export function addWeeks(fridayIso: string, delta: number): string {
  const d = parse(fridayIso);
  d.setDate(d.getDate() + delta * 7);
  return iso(d);
}

// The Friday-start weeks that cover a month ('YYYY-MM') — a calendar grid that
// spills into the prior/next month at the edges, like the bookings calendar.
export function monthGridWeeks(monthIso: string): string[][] {
  const [y, m] = monthIso.split('-').map(Number);
  const year = y ?? new Date().getFullYear();
  const month = m ?? 1; // 1-12
  const firstOfMonth = `${monthIso}-01`;
  // Last day of the month: day 0 of the next month.
  const lastDate = new Date(year, month, 0).getDate();
  const lastOfMonth = `${monthIso}-${String(lastDate).padStart(2, '0')}`;

  const firstFriday = startOfWeek(firstOfMonth);
  const lastFriday = startOfWeek(lastOfMonth);
  const weeks: string[][] = [];
  for (let wk = firstFriday; ; wk = addWeeks(wk, 1)) {
    weeks.push(weekDates(wk));
    if (wk === lastFriday) break;
  }
  return weeks;
}

// Month label for the header, e.g. "June 2026".
export function monthLabel(monthIso: string): string {
  const [y, m] = monthIso.split('-').map(Number);
  return new Date(y ?? 2026, (m ?? 1) - 1, 1).toLocaleDateString('en-IE', { month: 'long', year: 'numeric' });
}

// The 'YYYY-MM' an ISO date falls in.
export function monthIsoOf(dateIso: string): string {
  return dateIso.slice(0, 7);
}

// Shift a 'YYYY-MM' by N months.
export function addMonths(monthIso: string, delta: number): string {
  const [y, m] = monthIso.split('-').map(Number);
  const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// A human range label for the week WITH its year, e.g. "5–11 Jun 2026" — the
// roster's month grid, where a week block needs to say which year it is in.
// The range itself is formatted by weekRangeLabel; this only adds the year, so
// the two can never disagree about when to repeat a month.
export function weekLabel(mondayIso: string): string {
  const days = weekDates(mondayIso);
  return `${weekRangeLabel(days)} ${parse(days[6]!).getFullYear()}`;
}

// "This week" / "Next week" / "4–10 Sep" — how a person refers to a week out
// loud, which is what the On-duty panel's header shows. Lives here rather than
// in the panel so the roster can read it too and the two can never name the
// same week differently.
export function weekName(offset: number, days: string[]): string {
  // ONE FACT PER LABEL. The three weeks a person names in conversation get the
  // name and nothing else — a date range printed beside "This week" is the same
  // week said twice, and the eye has to check whether the two agree. Every other
  // week gets its dates rather than a count, because "In 7 weeks" is arithmetic
  // you have to finish before you know what you are looking at (Freddi,
  // 2026-08-17).
  if (offset === 0) return 'This week';
  if (offset === 1) return 'Next week';
  if (offset === -1) return 'Last week';
  return weekRangeLabel(days);
}

// "14–20 Aug", or "28 Aug – 3 Sep" when the week straddles two months. The
// month is stated once where it can be: a week sits inside one month six times
// in seven, and repeating it there is noise in a header built to be glanced at.
// Reads the ENDS of the array, so it does not care how many dates are in it.
export function weekRangeLabel(days: string[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return '';
  const a = parse(first);
  const b = parse(last);
  const monA = MONTHS_ABBR[a.getMonth()];
  const monB = MONTHS_ABBR[b.getMonth()];
  return monA === monB && a.getFullYear() === b.getFullYear()
    ? `${a.getDate()}–${b.getDate()} ${monB}`
    : `${a.getDate()} ${monA} – ${b.getDate()} ${monB}`;
}

// Whole weeks from one week-start to another, signed — negative for a week in
// the past. Exact at any distance.
//
// This replaces a loop that walked outward from today one week at a time and
// gave up after 260 iterations, then fell through to zero — so a week more than
// five years out was labelled "This week" (PR #164, claude P3). Quietly wrong,
// which is the worst kind: nothing on the screen admitted the label had stopped
// meaning anything. daysBetween is UTC-based, so a span crossing a
// daylight-saving boundary is still a whole number of weeks.
export function weeksBetween(fromWeekStart: string, toWeekStart: string): number {
  return Math.round(daysBetween(fromWeekStart, toWeekStart) / 7);
}
